/**
 * Password Change Form Component
 * Allows users to change their own password with validation and feedback
 */

import { useState, useEffect } from 'react';
import {
  Button,
  Field,
  Input,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { LockClosedRegular } from '@fluentui/react-icons';
import { Alert } from '../common/Alert';
import { SelfServiceApiService } from '../../services/selfServiceApi';
import type { PasswordChangeRequest } from '../../types/selfService';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    boxShadow: tokens.shadow4,
    padding: tokens.spacingVerticalXL,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  icon: {
    fontSize: '32px',
    color: tokens.colorNeutralForeground2,
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    margin: 0,
  },
  mainContent: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalXXXL,
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  buttonContainer: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
  requirementsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  requirementsTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  requirementsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  requirementItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase300,
  },
  requirementIcon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  requirementMet: {
    color: tokens.colorPaletteGreenForeground1,
  },
  requirementNotMet: {
    color: tokens.colorPaletteRedForeground1,
  },
  infoText: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
    marginTop: tokens.spacingVerticalM,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXXS,
  },
});

interface FormErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

interface PasswordRequirements {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
}

export function PasswordChangeForm() {
  const styles = useStyles();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Calculate password requirements in real-time
  const passwordRequirements: PasswordRequirements = {
    minLength: newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
  };

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (!passwordRequirements.minLength) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!passwordRequirements.hasUppercase) {
      newErrors.newPassword = 'Password must contain an uppercase letter';
    } else if (!passwordRequirements.hasLowercase) {
      newErrors.newPassword = 'Password must contain a lowercase letter';
    } else if (!passwordRequirements.hasNumber) {
      newErrors.newPassword = 'Password must contain a number';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    // Reset form
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const renderRequirement = (met: boolean, text: string) => (
    <li className={styles.requirementItem}>
      <span
        className={`${styles.requirementIcon} ${
          met ? styles.requirementMet : styles.requirementNotMet
        }`}
      >
        {met ? '✓' : '✗'}
      </span>
      <span style={{ color: met ? tokens.colorNeutralForeground1 : tokens.colorNeutralForeground2 }}>
        {text}
      </span>
    </li>
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const request: PasswordChangeRequest = {
        currentPassword,
        newPassword,
      };

      const response = await SelfServiceApiService.changePassword(request);

      if (response.success) {
        setSuccessMessage(response.message);
        // Clear form on success
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setErrors({});
      } else {
        setErrorMessage('Failed to change password. Please try again.');
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Failed to change password. Please check your current password and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Header Section */}
        <div className={styles.header}>
          <div className={styles.titleContainer}>
            <LockClosedRegular className={styles.icon} />
            <h2 className={styles.title}>Change Password</h2>
          </div>
          <p className={styles.subtitle}>
            Choose a unique password to protect your account
          </p>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <Alert intent="success" data-testid="success-message">
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert intent="error" data-testid="error-message">
            {errorMessage}
          </Alert>
        )}

        {/* Main Content: Form and Requirements */}
        <div className={styles.mainContent}>
        {/* Left Column: Form */}
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.fieldContainer}>
            <Field
              label="Current Password"
              required
              validationState={errors.currentPassword ? 'error' : 'none'}
            >
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={isLoading}
                placeholder="Type your current password"
              />
            </Field>
            {errors.currentPassword && (
              <span className={styles.errorText} role="alert">
                {errors.currentPassword}
              </span>
            )}
          </div>

          <div className={styles.fieldContainer}>
            <Field
              label="New Password"
              required
              validationState={errors.newPassword ? 'error' : 'none'}
            >
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                placeholder="Type your new password"
              />
            </Field>
            {errors.newPassword && (
              <span className={styles.errorText} role="alert">
                {errors.newPassword}
              </span>
            )}
          </div>

          <div className={styles.fieldContainer}>
            <Field
              label="Confirm New Password"
              required
              validationState={errors.confirmPassword ? 'error' : 'none'}
            >
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                placeholder="Retype your new password"
              />
            </Field>
            {errors.confirmPassword && (
              <span className={styles.errorText} role="alert">
                {errors.confirmPassword}
              </span>
            )}
          </div>

          <div className={styles.buttonContainer}>
            <Button appearance="secondary" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button appearance="primary" type="submit" disabled={isLoading}>
              {isLoading ? 'Changing Password...' : 'Save'}
            </Button>
          </div>
        </form>

        {/* Right Column: Password Requirements */}
        <div className={styles.requirementsContainer}>
          <h3 className={styles.requirementsTitle}>Password should contain:</h3>
          <ul className={styles.requirementsList}>
            {renderRequirement(passwordRequirements.minLength, 'At least 8 characters')}
            {renderRequirement(passwordRequirements.hasUppercase, 'Uppercase letter')}
            {renderRequirement(passwordRequirements.hasLowercase, 'Lowercase letter')}
            {renderRequirement(passwordRequirements.hasNumber, 'Number')}
          </ul>
          <p className={styles.infoText}>
            Password cannot be common or easily guessable
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}
